import React from 'react';
import BusinessNameInput from './BusinessNameInput';
import GatewayIdInput from './GatewayIdInput';
import ProjectedVolumeField from './ProjectedVolumeField';
import { AUTHORIZE_NET_COMPANY, CYBERSOURCE_COMPANY } from '../../../utils/constants';

/**
 * BusinessDetails Component
 * Renders business-related fields and selects for the merchant form.
 * Handles conditional rendering based on config and state.
 * Props:
 *  - state: form state object
 *  - config: configuration object for form
 *  - handleChange: input change handler
 *  - handleSelectChange: select change handler
 */
const BusinessDetails = ({ state, config, handleChange, handleSelectChange }) => {
  // Returns column width class based on selected company
  const getColumnWidth = () => (config.company === AUTHORIZE_NET_COMPANY ? 'col-lg-2' : 'col-lg-3');
  
  // Custom handler for Account Executive changes
  const handleAccountExecutiveChange = (e) => {
    const value = e.target.value;
    // Only update accountExecutive field
    handleChange({
      target: {
        name: 'accountExecutive',
        value: value
      }
    });
  };

  /**
   * Reusable select dropdown with validation tooltip
   */
  const SelectInput = ({ id, label, selectLabel, name, value, error, onChange, options, required = false, showSuccessValidation = true }) => (
    <div className={getColumnWidth()}>
      <div className="form-floating mb-4 position-relative">
        <select
          className={`form-select ${error ? 'is-invalid' : ''}`}
          id={id}
          name={name}
          value={value || `${label}`}
          onChange={onChange}
          required={required}
        >
          <option value={`${label}`} disabled selected hidden>{`${label}`}</option>
          {options.map(opt => (
            <option key={opt.value || opt.name} value={opt.value || opt.name}>
              &nbsp;{opt.label || opt.name}
            </option>
          ))}
        </select>
        <label htmlFor={id}>{selectLabel}</label>
        {/* Show error tooltip if validation fails */}
        {error && <div className="invalid-tooltip">{error}</div>}
        {/* Show success tooltip if field is valid and showSuccessValidation is true */}
        {value && !error && showSuccessValidation && <div className="valid-tooltip">{`${label} selected`}</div>}
      </div>
    </div>
  );

  return (
    <div className="row mt-4">
      {/* Business Name input */}
      <div className={getColumnWidth()}>
        <BusinessNameInput state={state} handleChange={handleChange} />
      </div>

      {/* Gateway ID input, only if required by config */}
      {config.hasGateway && (
        <div className={getColumnWidth()}>
          <GatewayIdInput state={state} handleChange={handleChange} />
        </div>
      )}

      {/* Sales Rep select, only if not a simple form */}
      {!config.hasSimpleForm && state.company !== CYBERSOURCE_COMPANY && (
        <SelectInput
          id="salesRep"
          label="Select Sales Rep"
          selectLabel="Sales Representative"
          name="salesRep"
          value={state.salesRep}
          error={state.errors.salesRep}
          onChange={handleSelectChange}
          required
          showSuccessValidation={config.title !== "Authorize.net"}
          options={
            config.hasCompanySelect
              ? (state.company === AUTHORIZE_NET_COMPANY
                  ? state[AUTHORIZE_NET_COMPANY]
                  : state[CYBERSOURCE_COMPANY]
                ).map(user => ({ name: user.name }))
              : state.preferredAgent.map(user => ({ name: user.name }))
          }
        />
      )}

      {/* Account Executive select, only if company select is enabled */}
      {config.hasCompanySelect && state.company !== AUTHORIZE_NET_COMPANY && (
        <SelectInput
          id="accountExecutive"
          label="Select Account Executive"
          selectLabel="Account Executive"
          name="accountExecutive"
          value={state.accountExecutive}
          error={state.errors.accountExecutive}
          onChange={handleAccountExecutiveChange}
          showSuccessValidation={config.title !== "Authorize.net"}
          options={state[CYBERSOURCE_COMPANY].map(user => ({ name: user.name }))}
        />
      )}

      {/* Submitting Rep select, only for Authorize.net company */}
      {config.hasCompanySelect && state.company === AUTHORIZE_NET_COMPANY && (
        <SelectInput
          id="subRep"
          label="Submitting Rep"
          selectLabel="Submitting Rep"
          name="subRep"
          value={state.subRep}
          error={state.errors.subRep}
          onChange={handleChange}
          showSuccessValidation={config.title !== "Authorize.net"}
          options={state[AUTHORIZE_NET_COMPANY].map(user => ({ name: user.name }))}
        />
      )}

      {/* Location select, only if enabled in config */}
      {config.hasLocation && (
        <SelectInput
          id="location"
          label="Select Location"
          selectLabel="Location"
          name="currency"
          value={state.currency}
          error={state.errors.currency}
          onChange={handleChange}
          showSuccessValidation={config.title !== "Authorize.net"}
          options={[
            { value: 'United States', label: 'United States' },
            { value: 'Canada', label: 'Canada' },
            { value: 'Australia', label: 'Australia' },
            { value: 'Puerto Rico/USVI/Guam', label: 'Puerto Rico/USVI/Guam' },
          ]}
        />
      )}

      {/* Projected Volume field, only for CyberSource company */}
      {config.hasCompanySelect && state.company === CYBERSOURCE_COMPANY && (
        <ProjectedVolumeField state={state} handleChange={handleChange} />
      )}
    </div>
  );
};

export default BusinessDetails;
